import cx from 'classnames';
import Portal from 'react-overlays/Portal';
import { useSelector } from 'react-redux';
import {
  curatorActions,
  selectedIsImageScanSearchFile,
  selectedIsImageScanSearchOnboardingData,
} from '../../redux/slicers/admin/curatorSlicer';
import styles from './ImageScanSearch.module.scss';
import Button from '../Button';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop, { Crop, makeAspectCrop, centerCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';
import { warningToast } from '../../helpers/toastHelper';
import { dispatcher } from '../../helpers/projectHelper';
import { icons } from '../../assets';
import { objectToFormData, wait } from '../../helpers/jsHelper';
import { TooltipOverlay } from '../../common/TooltipOverlay/TooltipOverlay';
import { useUserContext } from '../../contexts/UserContext';
import { apiUpdateUserDetails } from '../../helpers/api';
import { isCuratorPage } from '../../helpers/urlHelper';
import Loader from '../Loader/Loader';
import { CuratorSidebarLayout } from '../admin/curator/CuratorSidebarLayout/CuratorSidebarLayout';
const { CrossIcon } = icons;

const TO_RADIANS = Math.PI / 180;

async function canvasPreview({ image, canvas, crop, scale = 1, rotate = 0 }) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  // devicePixelRatio slightly increases sharpness on retina devices
  // at the expense of slightly slower render times and needing to
  // size the image back down if you want to download/upload and be
  // true to the images natural size.
  const pixelRatio = 0.75; // window.devicePixelRatio || 1;
  // const pixelRatio = 1

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  // console.log('DEBUG_ canvasPreview', {
  //   canvasWidth: canvas.width,
  //   canvasHeight: canvas.height,
  //   scaleX,
  //   scaleY,
  //   width: image.width,
  //   height: image.height,
  //   naturalWidth: image.naturalWidth,
  //   naturalHeight: image.naturalHeight,
  // });

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  // 5) Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY);
  // 4) Move the origin to the center of the original position
  ctx.translate(centerX, centerY);
  // 3) Rotate around the origin
  ctx.rotate(rotateRads);
  // 2) Scale the image
  ctx.scale(scale, scale);
  // 1) Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();

  const base64 = canvas.toDataURL('image/jpeg');
  if (base64) {
    return base64;
  }
}

function useDebounceEffect(fn, waitTime, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
  }, deps);
}

export const SearchFromScannedImage = (props) => {
  const file = useSelector(selectedIsImageScanSearchFile);
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current = document.querySelector('body');
  }, []);

  if (!file) return null;
  if (props?.isPortal) {
    if (!props?.show) return null;
    return (
      <Portal container={bodyRef}>
        <SearchFromScannedImageUI file={file} {...props} />
      </Portal>
    );
  }
  return <SearchFromScannedImageUI file={file} {...props} />;
};

const SearchFromScannedImageUI = ({
  refetchList = null,
  file,
  isCurator = false,
  loading = false,
  onClose = null,
  show = false,
}) => {
  const { currentUser, updateUserData } = useUserContext();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [base64Url, setBase64Url] = useState(null);
  const { currentStep, isActive } = useSelector(selectedIsImageScanSearchOnboardingData);
  const [left, setLeft] = useState(0);

  // const aspect = 1; // 16 / 9;

  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 80,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  };

  const setIconPosition = () => {
    const containerWidth = containerRef?.current?.clientWidth;
    window.containerRef = containerRef?.current;
    const l = 96 + 420 + containerWidth - 3;
    setLeft(l);
  };

  useEffect(() => {
    setIconPosition();
    window.addEventListener('resize', setIconPosition);
    return () => window.removeEventListener('resize', setIconPosition);
  }, []);

  useEffect(() => {
    if (isCurator && show && file?.croppedImageData && file?.completedCropData) {
      setCroppedImage(file.croppedImageData);
      setCompletedCrop(file.completedCropData);
    }
  }, [isCurator, show, file?.croppedImageData, file?.completedCropData]);

  const onImageLoad = async (e) => {
    const { width, height } = e.currentTarget;
    const aspect = width / height;
    const croppedAspect = centerAspectCrop(width, height, aspect);

    setImageLoaded(true);
    setIconPosition();
    
    if (!file?.croppedImageData && !file?.completedCropData) {
      dispatcher(
        curatorActions.setImageScanSearchFile({
          ...file,
          croppedImageData: croppedAspect,
          completedCropData: convertToPixelCrop(croppedAspect, width, height),
        })
      );
      setCroppedImage(croppedAspect);
      setCompletedCrop(convertToPixelCrop(croppedAspect, width, height));
      await wait(500);
      refetchList?.();
    }

  };

  useDebounceEffect(
    async () => {
      if (
        imageLoaded &&
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        const imgResponse = await canvasPreview({
          image: imgRef.current,
          canvas: previewCanvasRef.current,
          crop: completedCrop,
        });

        if (imgResponse) {
          const img = imgResponse?.split('base64,')?.[1];
          setBase64Url?.(img);
          dispatcher(
            curatorActions.setImageScanSearchFile({ ...file, croppedImage: img, embedding: null })
          );
          setIconPosition();
        }
      }
    },
    100,
    [completedCrop, imageLoaded]
  );

  useEffect(() => {
    if (!loading) setSearchLoading(false);
  }, [loading]);

  const onSearch = async () => {
    if (!imageLoaded) return;
    if (!base64Url) {
      warningToast('Please select the image to search.');
      return;
    }

    setSearchLoading(true);

    if (isActive && currentStep === 2)
      dispatcher(curatorActions.setImageScanSearchOnboardingData({ currentStep: currentStep + 1 }));

    dispatcher(
      curatorActions.setImageScanSearchFile({
        ...file,
        croppedImageData: croppedImage,
        completedCropData: completedCrop,
      })
    );
    await wait(500);
    refetchList?.();
    setSearchLoading(false);
  };

  const handleClose = async () => {
    dispatcher(curatorActions.setImageScanSearchFile(null));
    onClose?.();

    if (isActive && currentStep === 3) {
      dispatcher(
        curatorActions.setImageScanSearchOnboardingData({
          currentStep: currentStep + 1,
          isActive: false,
          isCompleted: true,
        })
      );

      const payload = {
        other_info: JSON.stringify({
          ...currentUser?.user?.other_info,
          showImageSearchOnboarding: true,
        }),
      };
      const formData = objectToFormData(payload);
      await apiUpdateUserDetails(formData);
      updateUserData();
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        className={cx(styles.imageSearchWrapper, isCurator ? styles.inCurator : '')}>
        {!imageLoaded ? <Loader loading position="absolute" /> : null}
        <div className={cx(styles.innerImageSearchWrapper)}>
          <TooltipOverlay step={3} className={styles.close}>
            <CrossIcon onClick={handleClose} />
          </TooltipOverlay>
          <ReactCrop
            className={styles.imageCropper}
            crop={croppedImage}
            onChange={(_, percentCrop) => setCroppedImage(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            minHeight={25}>
            <img ref={imgRef} className="img-fluid" src={file.url} onLoad={onImageLoad} />
          </ReactCrop>
          <p className={styles.text}>
            {isCuratorPage()
              ? 'Adjust the cropping area to search for the Product'
              : 'Adjust the cropping area to search for the Prop'}
          </p>

          <TooltipOverlay step={2}>
            <Button
              onClick={onSearch}
              disabled={!croppedImage || searchLoading || loading}
              loading={searchLoading && loading}
              size="lg"
              color="dark">
              Search
            </Button>
          </TooltipOverlay>
        </div>

        {completedCrop ? (
          <canvas
            ref={previewCanvasRef}
            style={{
              position: 'fixed',
              left: '1rem',
              bottom: '1rem',
              zIndex: 1000,
              display: 'none',
              objectFit: 'contain',
              width: completedCrop.width,
              height: completedCrop.height,
            }}
          />
        ) : null}
      </div>
      {isCurator ? (
        <CuratorSidebarLayout.Button style={{ left: left }} location="imagesearch" disabled={loading} />
      ) : null}
    </>
  );
};
